import { lazy, Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { AppLayout } from '@/layout/AppLayout';
import { HomePage } from '@/features/home/HomePage';
import { ComuniIndexPage } from '@/features/comuni/ComuniIndexPage';
import { NotFoundPage } from '@/features/pages/NotFoundPage';
import { Placeholder } from '@/features/pages/Placeholder';
import { LegacyRedirect } from './LegacyRedirect';
import { ALIAS_PAGINE_FISSE, PAGINE_FISSE } from './pagineFisse';

/**
 * Tabella delle rotte — nuova struttura URL (decisione 18).
 * ---------------------------------------------------------------------------
 * Regole, tutte visibili qui:
 *   · nessun prefisso `/puglia/`
 *   · nessuna estensione `.html`
 *   · trailing slash sulle rotte di dettaglio
 *   · **slug comune con underscore** (`acquaviva_delle_fonti`) — vincolo 3:
 *     convertirli a trattini è breaking su 257 URL indicizzate
 *
 * La pagina comune è caricata in `lazy()`: è la più pesante (10 pannelli + il
 * chunk Leaflet) e la home non deve pagarne il costo.
 *
 * I segnaposto sono dichiarati come tali: la rotta esiste già così i 301 di
 * Nginx puntano a qualcosa, ma la pagina dice che il contenuto va ancora
 * portato. Non è un vuoto travestito da pagina finita.
 */

const ComunePage = lazy(() =>
  import('@/features/comune/ComunePage').then((m) => ({ default: m.ComunePage })),
);
/* Le altre pagine di contenuto sono anch'esse lazy: la home carica solo sé
   stessa. Ogni feature è un chunk. */
const ProvincePage = lazy(() =>
  import('@/features/province/ProvincePage').then((m) => ({ default: m.ProvincePage })),
);
const ProvinciaPage = lazy(() =>
  import('@/features/province/ProvinciaPage').then((m) => ({ default: m.ProvinciaPage })),
);
const EventiPage = lazy(() =>
  import('@/features/eventi/EventiPage').then((m) => ({ default: m.EventiPage })),
);
const EventoPage = lazy(() =>
  import('@/features/eventi/EventoPage').then((m) => ({ default: m.EventoPage })),
);
const ProponiEventoPage = lazy(() =>
  import('@/features/eventi/ProponiEventoPage').then((m) => ({
    default: m.ProponiEventoPage,
  })),
);
const ItinerariPage = lazy(() =>
  import('@/features/itinerari/ItinerariPage').then((m) => ({ default: m.ItinerariPage })),
);
const RottaSlugItinerari = lazy(() =>
  import('@/features/itinerari/RottaSlugItinerari').then((m) => ({
    default: m.RottaSlugItinerari,
  })),
);
const SocialKitPage = lazy(() =>
  import('@/features/eventi/SocialKitPage').then((m) => ({ default: m.SocialKitPage })),
);
const NewsPage = lazy(() =>
  import('@/features/news/NewsPage').then((m) => ({ default: m.NewsPage })),
);
const AttivitaProponiPage = lazy(() =>
  import('@/features/attivita/AttivitaProponiPage').then((m) => ({
    default: m.AttivitaProponiPage,
  })),
);
const AttivitaModificaPage = lazy(() =>
  import('@/features/attivita/AttivitaModificaPage').then((m) => ({
    default: m.AttivitaModificaPage,
  })),
);
const JobCoinPage = lazy(() =>
  import('@/features/rete/JobCoinPage').then((m) => ({ default: m.JobCoinPage })),
);
const StatusPage = lazy(() =>
  import('@/features/progetto/StatusPage').then((m) => ({ default: m.StatusPage })),
);
const KioskLauncherPage = lazy(() =>
  import('@/features/kiosk/KioskLauncherPage').then((m) => ({
    default: m.KioskLauncherPage,
  })),
);
const NewsBandoPage = lazy(() =>
  import('@/features/news/BandoPage').then((m) => ({ default: m.BandoPage })),
);
const OpportunitaPage = lazy(() =>
  import('@/features/news/OpportunitaPage').then((m) => ({ default: m.OpportunitaPage })),
);
const AttivitaElencoPage = lazy(() =>
  import('@/features/attivita/AttivitaElencoPage').then((m) => ({
    default: m.AttivitaElencoPage,
  })),
);
const AttivitaDettaglioPage = lazy(() =>
  import('@/features/attivita/AttivitaDettaglioPage').then((m) => ({
    default: m.AttivitaDettaglioPage,
  })),
);
const ReteTerritorioPage = lazy(() =>
  import('@/features/rete/ReteTerritorioPage').then((m) => ({
    default: m.ReteTerritorioPage,
  })),
);
const AssistentePage = lazy(() =>
  import('@/features/assistenza/AssistentePage').then((m) => ({
    default: m.AssistentePage,
  })),
);
const DocsApiPage = lazy(() =>
  import('@/features/docs/DocsApiPage').then((m) => ({ default: m.DocsApiPage })),
);
const FlipbookPage = lazy(() =>
  import('@/features/documenti/FlipbookPage').then((m) => ({ default: m.FlipbookPage })),
);
const CercaPage = lazy(() =>
  import('@/features/cerca/CercaPage').then((m) => ({ default: m.CercaPage })),
);
const MareELidiPage = lazy(() =>
  import('@/features/territorio/MareELidiPage').then((m) => ({
    default: m.MareELidiPage,
  })),
);
const PartecipaPage = lazy(() =>
  import('@/features/rete/PartecipaPage').then((m) => ({ default: m.PartecipaPage })),
);
const PaginaRedazionalePage = lazy(() =>
  import('@/features/pagine/PaginaRedazionalePage').then((m) => ({
    default: m.PaginaRedazionalePage,
  })),
);

/* Back office (decisione 47): stessa SPA, chunk caricati solo su /redazione/. */
const RedazioneLayout = lazy(() =>
  import('@/features/redazione/RedazioneLayout').then((m) => ({
    default: m.RedazioneLayout,
  })),
);
const PagineListPage = lazy(() =>
  import('@/features/redazione/PagineListPage').then((m) => ({
    default: m.PagineListPage,
  })),
);
const EditorPagina = lazy(() =>
  import('@/features/redazione/EditorPagina').then((m) => ({ default: m.EditorPagina })),
);
const ModerazionePage = lazy(() =>
  import('@/features/redazione/ModerazionePage').then((m) => ({
    default: m.ModerazionePage,
  })),
);
const UtentiPage = lazy(() =>
  import('@/features/redazione/UtentiPage').then((m) => ({ default: m.UtentiPage })),
);

/* Vocabolari aperti (decisione 112): chunk a parte, lo apre solo un admin. */
const VocabolariPage = lazy(() =>
  import('@/features/redazione/VocabolariPage').then((m) => ({ default: m.VocabolariPage })),
);
/* Dashboard redazionale (decisioni 73-86): liste ed editor GENERICI guidati
   dal catalogo `/redazione/risorse/`, più le schermate dedicate. */
const HomeDashboard = lazy(() =>
  import('@/features/redazione/HomeDashboard').then((m) => ({ default: m.HomeDashboard })),
);
const ListaGenerica = lazy(() =>
  import('@/features/redazione/ListaGenerica').then((m) => ({ default: m.ListaGenerica })),
);
const EditorGenerico = lazy(() =>
  import('@/features/redazione/EditorGenerico').then((m) => ({ default: m.EditorGenerico })),
);
const LibreriaMedia = lazy(() =>
  import('@/features/redazione/LibreriaMedia').then((m) => ({ default: m.LibreriaMedia })),
);
const NotifichePage = lazy(() =>
  import('@/features/redazione/NotifichePage').then((m) => ({ default: m.NotifichePage })),
);
const AuditPage = lazy(() =>
  import('@/features/redazione/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const ImportCsvPage = lazy(() =>
  import('@/features/redazione/ImportCsvPage').then((m) => ({ default: m.ImportCsvPage })),
);
const ImportatoriPage = lazy(() =>
  import('@/features/redazione/ImportatoriPage').then((m) => ({
    default: m.ImportatoriPage,
  })),
);
/* Configuratore automatico di itinerari (decisioni 221-227, 230+): wizard
   riservato a revisori e amministratori. Chunk a parte: porta Leaflet. */
const ConfiguratoreItinerari = lazy(() =>
  import('@/features/redazione/ConfiguratoreItinerari').then((m) => ({
    default: m.ConfiguratoreItinerari,
  })),
);

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<p className="ic-container">Caricamento…</p>}>{children}</Suspense>;
}

/**
 * Rotta → pagina redazionale in database (decisioni 28–42).
 *
 * Lo slug è CONCORDATO col backend (decisione 41): la lista completa sta in
 * `backend/apps/core/importatori/pagine.py` (`PAGINE_LEGACY`) e nel
 * contratto (`docs/contratto-api-v2.md`). La redazione riempie e pubblica
 * dal back office; qui non c'è contenuto, solo l'aggancio.
 */
function paginaDb(slug: string) {
  return (
    <Lazy>
      <PaginaRedazionalePage slug={slug} />
    </Lazy>
  );
}

/**
 * Rotta residuale `/p/{slug}` (decisione 71): serve le pagine CREATE dalla
 * redazione, con slug fuori dalle rotte concordate. Una pagina nuova va
 * online senza deploy; se non è pubblicata la rotta dice «contenuto non
 * disponibile», come ogni altra.
 *
 * Se lo slug HA una rotta fissa, qui si REINDIRIZZA (decisione 196): stesso
 * criterio di `/eventi/live/` — un solo URL canonico, niente doppioni
 * indicizzabili. È un redirect client (la SPA non può emettere un 301); il
 * 301 vero potrà farlo Nginx a partire dall'artefatto generato
 * (`docs/segmenti-fissi.generated.json`).
 */
function PaginaLibera() {
  const { slug = '' } = useParams();
  const rottaFissa = PAGINE_FISSE[slug];
  if (rottaFissa) return <Navigate to={rottaFissa} replace />;
  return (
    <Lazy>
      <PaginaRedazionalePage slug={slug} />
    </Lazy>
  );
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <HomePage /> },

      // ---------- Comuni ----------
      { path: 'comuni/', element: <ComuniIndexPage /> },
      {
        // Lo slug ammette underscore: `[a-z0-9_]+`. React Router non filtra
        // per regex, il controllo è nel componente (404 se non esiste).
        path: 'comuni/:slug/',
        element: (
          <Lazy>
            <ComunePage />
          </Lazy>
        ),
      },

      // ---------- Province ----------
      {
        path: 'province/',
        element: (
          <Lazy>
            <ProvincePage />
          </Lazy>
        ),
      },
      {
        // Il parametro è lo SLUG (`/province/bari/`), la forma a cui puntano
        // i 301 legacy. `provinciaDaSlug` tollera anche la sigla minuscola.
        path: 'province/:sigla/',
        element: (
          <Lazy>
            <ProvinciaPage />
          </Lazy>
        ),
      },

      // ---------- Eventi ----------
      // REVOCA (31/07/2026, decisione utente — allineamento visual-diff,
      // voce 19): il calendario a griglia SI FA, con la resa del riferimento.
      // Resta fermo il vincolo 4: gli eventi senza giorno dichiarato NON
      // vengono spalmati su caselle inventate — compaiono nel conteggio del
      // periodo dichiarato, e le caselle senza eventi restano vuote.
      // (Decisione precedente, revocata: «niente calendario, 362 eventi su
      // 362 senza data esatta».)
      {
        path: 'eventi/',
        element: (
          <Lazy>
            <EventiPage />
          </Lazy>
        ),
      },
      {
        // "Eventi in corso" del portale statico: confluisce sull'elenco
        // eventi con un REDIRECT (decisione utente 31/07/2026, voce 21):
        // un solo URL canonico, niente doppioni indicizzabili.
        path: 'eventi/live/',
        element: <Navigate to="/eventi/" replace />,
      },
      {
        path: 'eventi/proponi/',
        element: (
          <Lazy>
            <ProponiEventoPage />
          </Lazy>
        ),
      },
      {
        path: 'eventi/:slug/',
        element: (
          <Lazy>
            <EventoPage />
          </Lazy>
        ),
      },
      {
        // Kit dimostrativo dichiarato (filigrana DEMO anche nei PNG).
        path: 'eventi/social-kit/',
        element: (
          <Lazy>
            <SocialKitPage />
          </Lazy>
        ),
      },

      // ---------- Pagine redazionali con rotta fissa (decisioni 28–42) ------
      // Le rotte si GENERANO dalla tabella slug→percorso di `pagineFisse.ts`
      // (decisione 196): la stessa tabella decide il `rel="canonical"` della
      // pagina, quindi rotta e canonico non possono divergere (§I.13).
      // Coprono servizi, territorio, rete civica, progetto e legale.
      ...Object.entries(PAGINE_FISSE).map(([slug, percorso]) => ({
        path: percorso.slice(1),
        element: paginaDb(slug),
      })),
      // Alias storici (es. `/accessibilita/`): stessa pagina della rotta
      // fissa, che resta il canonico dichiarato in pagina.
      ...Object.entries(ALIAS_PAGINE_FISSE).map(([percorso, slug]) => ({
        path: percorso.slice(1),
        element: paginaDb(slug),
      })),

      // ---------- Territorio (pagine-dato e redirect) ----------
      {
        // PAGINA-DATO (coda rimisura 31/07/2026, voce 29): gli stabilimenti
        // da GET /strutture-ricettive/; i testi di cornice restano contenuto
        // redazionale della pagina `territorio-mare-e-lidi`.
        path: 'territorio/mare-e-lidi/',
        element: (
          <Lazy>
            <MareELidiPage />
          </Lazy>
        ),
      },
      {
        // turismo.html era la terza superficie dello stesso assistente AI:
        // confluisce sull'assistente unico col contesto turistico
        // (decisione utente 31/07/2026).
        path: 'territorio/turismo/',
        element: <Navigate to="/assistenza/assistente/?contesto=turismo" replace />,
      },
      {
        // Scelta RISOLTA (decisione utente 31/07/2026): FLIPBOOK. Il backend
        // serve l'export originale (32 pagine JPG) via GET /api/v2/documenti/
        // /mappa-multimediale-brindisi/; lo sfogliatore è ricostruito
        // accessibile (tastiera, annuncio pagina, testo per pagina).
        // Il ramo «mappa POI» resta coperto dai dati comune esistenti.
        path: 'territorio/mappa-multimediale/',
        element: (
          <Lazy>
            <FlipbookPage />
          </Lazy>
        ),
      },

      // ---------- Itinerari ----------
      // Le card linkano la pagina locale: nel portale statico ic-cammini.js
      // metteva la fonte esterna nell'href e le 15 pagine erano irraggiungibili.
      {
        path: 'itinerari/',
        element: (
          <Lazy>
            <ItinerariPage />
          </Lazy>
        ),
      },
      {
        // UNA rotta per due pagine: il dettaglio di un itinerario e la pagina
        // di una categoria hanno la stessa forma di URL (contratto §I.3 contro
        // i 301 del portale statico). A decidere è l'elenco piatto delle
        // categorie, non un tentativo con ripiego sul 404: vedi
        // `RottaSlugItinerari` per la scelta e la sua conseguenza.
        path: 'itinerari/:slug/',
        element: (
          <Lazy>
            <RottaSlugItinerari />
          </Lazy>
        ),
      },

      // ---------- Attività ----------
      {
        // 193 schede reali (pilota Brindisi) da GET /api/v2/attivita/,
        // base OSM ODbL: l'attribuzione share-alike si mostra in pagina.
        path: 'attivita/',
        element: (
          <Lazy>
            <AttivitaElencoPage />
          </Lazy>
        ),
      },
      {
        // Scheda attività (contratto del 31/07/2026). Le rotte statiche
        // proponi/modifica vincono sul parametro per ranking.
        path: 'attivita/:slug/',
        element: (
          <Lazy>
            <AttivitaDettaglioPage />
          </Lazy>
        ),
      },
      {
        // Wizard a 8 passi su POST /api/v2/contributi/ (tipo proposta_attivita).
        path: 'attivita/proponi/',
        element: (
          <Lazy>
            <AttivitaProponiPage />
          </Lazy>
        ),
      },
      {
        // Form unico su POST /api/v2/contributi/ (tipo modifica_attivita).
        path: 'attivita/modifica/',
        element: (
          <Lazy>
            <AttivitaModificaPage />
          </Lazy>
        ),
      },

      // ---------- Rete civica (le pagine redazionali sono generate sopra) ---
      {
        // Il MODULO del riferimento (coda rimisura 31/07/2026, voce 29):
        // POST /contributi/ con tipo `adesione_rete_civica`.
        path: 'rete-civica/partecipa/',
        element: (
          <Lazy>
            <PartecipaPage />
          </Lazy>
        ),
      },
      {
        // Wizard a 5 passi con anteprima live. L'INVIO è in assenza
        // dichiarata: il contratto (POST /api/v2/contributi/, tipo
        // registrazione_jobcoin) non è ancora attivo e la pagina lo dice —
        // niente finti successi. Le registrazioni Supabase non si migrano
        // (decisioni 22-23): anagrafica vuota.
        path: 'rete-civica/jobcoin/',
        element: (
          <Lazy>
            <JobCoinPage />
          </Lazy>
        ),
      },
      // Le CINQUE rotte gemelle della «Rete del territorio» (decisione
      // utente 31/07/2026): stesso corpo condiviso, cambia la scheda attiva
      // nella sotto-navigazione — gemelle nelle rotte, non nel codice.
      {
        path: 'rete-civica/lavoro/',
        element: (
          <Lazy>
            <ReteTerritorioPage sezione="lavoro" />
          </Lazy>
        ),
      },
      {
        // Variante intro del riferimento (lavoro-servizi/intro.html): stessa
        // pagina con la seconda fascia demo.
        path: 'rete-civica/lavoro/servizi/',
        element: (
          <Lazy>
            <ReteTerritorioPage sezione="lavoro" intro />
          </Lazy>
        ),
      },
      {
        path: 'rete-civica/volontariato/',
        element: (
          <Lazy>
            <ReteTerritorioPage sezione="volontariato" />
          </Lazy>
        ),
      },
      {
        path: 'rete-civica/start-up/',
        element: (
          <Lazy>
            <ReteTerritorioPage sezione="startup" />
          </Lazy>
        ),
      },
      {
        path: 'rete-civica/enti-terzo-settore/',
        element: (
          <Lazy>
            <ReteTerritorioPage sezione="ets" />
          </Lazy>
        ),
      },

      // ---------- Ricerca e assistenza ----------
      {
        // Client-side su comuni, eventi e itinerari. Le chiavi i18n della
        // pagina sono nuove (la statica era fuori dal circuito i18n) e vanno
        // tradotte: elencate in docs/i18n-migrazione.md.
        path: 'cerca/',
        element: (
          <Lazy>
            <CercaPage />
          </Lazy>
        ),
      },
      {
        // UNA superficie sola per le tre varianti dell'assistente (decisione
        // utente 31/07/2026): informativa AI Act art. 50 + esempi,
        // accessibile, NESSUN widget di terze parti. La conversazione è in
        // assenza dichiarata finché non c'è un contratto API.
        path: 'assistenza/assistente/',
        element: (
          <Lazy>
            <AssistentePage />
          </Lazy>
        ),
      },

      // ---------- News e opportunità ----------
      {
        // Elenco bandi/opportunità da /api/v2/bandi/ (alias /news/).
        path: 'news/',
        element: (
          <Lazy>
            <NewsPage />
          </Lazy>
        ),
      },
      {
        // Scheda bando per SLUG (gli id legacy n1..n9 collidono fra le
        // sorgenti e non si usano). Alias /bandi/{slug}/ lato API.
        path: 'news/:slug/',
        element: (
          <Lazy>
            <NewsBandoPage />
          </Lazy>
        ),
      },
      {
        // Finder delle opportunità: struttura del riferimento, palette navy
        // del portale (decisione utente 31/07/2026 — niente hero verde).
        path: 'opportunita/',
        element: (
          <Lazy>
            <OpportunitaPage />
          </Lazy>
        ),
      },

      // ---------- Progetto (le pagine redazionali sono generate sopra) ------
      {
        // Monitor dei servizi su GET /api/v2/health/ + endpoint reali,
        // misurati dal browser ogni 30 s. In fixtures la misura non esiste
        // e la pagina lo dichiara.
        path: 'progetto/status/',
        element: (
          <Lazy>
            <StatusPage />
          </Lazy>
        ),
      },

      // ---------- Kiosk ----------
      // (Le pagine legali, decisione 34, sono fra le rotte generate sopra;
      //  l'alias storico `/accessibilita/` viene da ALIAS_PAGINE_FISSE.)
      {
        // Launcher del totem: arma la modalità kiosk (KioskProvider) e
        // chiede il fullscreen DENTRO il click, come i browser esigono.
        path: 'kiosk/',
        element: (
          <Lazy>
            <KioskLauncherPage />
          </Lazy>
        ),
      },

      // ---------- Back office: vive su /redazione/ (decisioni 43-72) ----------
      // I vecchi URL admin del portale statico confluiscono lì.
      { path: 'admin/', element: <Navigate to="/redazione/" replace /> },
      {
        path: 'admin/moderazione/',
        element: <Navigate to="/redazione/moderazione/" replace />,
      },
      {
        path: 'admin/comune/',
        element: <Placeholder title="Editor scheda comune" source="puglia/admin/comune.html" />,
      },

      // ---------- Pagine create dalla redazione (decisione 71) ----------
      { path: 'p/:slug/', element: <PaginaLibera /> },

      // ---------- Documentazione API ----------
      // Rotta RINOMINATA da /api/docs/ (decisione utente 31/07/2026, voci
      // 1-2 del visual-diff): il prefisso /api/* è instradato al backend dal
      // reverse proxy, quindi la rotta React lì non era mai raggiungibile.
      {
        // Contenuti RISCRITTI su API v2 (decisione utente 31/07/2026): il
        // riferimento documentava la v1 dismessa (decisione 19) e non si
        // copia. Elenco endpoint generato dallo schema, yaml self-hosted.
        path: 'docs-api/',
        element: (
          <Lazy>
            <DocsApiPage />
          </Lazy>
        ),
      },
      // Il vecchio percorso resta come redirect client-side: utile in dev
      // (senza proxy davanti) e per i link salvati prima della rinomina.
      { path: 'api/docs/', element: <Navigate to="/docs-api/" replace /> },

      { path: '404', element: <NotFoundPage /> },

      // Catch-all: prima prova i redirect legacy, poi 404.
      { path: '*', element: <LegacyRedirect /> },
    ],
  },

  // ---------- Back office (decisione 47) ----------
  // Fuori da AppLayout: la redazione ha la sua shell, il suo guard di
  // sessione e un'interfaccia solo in italiano (decisione 49).
  {
    path: '/redazione/',
    element: (
      <Lazy>
        <RedazioneLayout />
      </Lazy>
    ),
    children: [
      // La home della dashboard prende l'indice: era l'elenco pagine, che
      // ora è una sezione fra le altre (`/redazione/pagine/`).
      {
        index: true,
        element: (
          <Lazy>
            <HomeDashboard />
          </Lazy>
        ),
      },

      // ---- Risorse generiche: UNA coppia lista/editor per 33 entità ----
      // L'ordine conta: `nuovo/` e `cestino/` prima di `:id/`, altrimenti
      // sarebbero letti come identificativi.
      {
        path: 'dati/:entita/',
        element: (
          <Lazy>
            <ListaGenerica />
          </Lazy>
        ),
      },
      {
        path: 'dati/:entita/cestino/',
        element: (
          <Lazy>
            <ListaGenerica cestino />
          </Lazy>
        ),
      },
      {
        path: 'dati/:entita/nuovo/',
        element: (
          <Lazy>
            <EditorGenerico />
          </Lazy>
        ),
      },
      {
        path: 'dati/:entita/:id/',
        element: (
          <Lazy>
            <EditorGenerico />
          </Lazy>
        ),
      },

      // ---- Schermate dedicate (gestione non generica) ----
      {
        path: 'pagine/',
        element: (
          <Lazy>
            <PagineListPage />
          </Lazy>
        ),
      },
      {
        path: 'pagine/:slug/',
        element: (
          <Lazy>
            <EditorPagina />
          </Lazy>
        ),
      },
      {
        path: 'media/',
        element: (
          <Lazy>
            <LibreriaMedia />
          </Lazy>
        ),
      },
      {
        path: 'moderazione/',
        element: (
          <Lazy>
            <ModerazionePage />
          </Lazy>
        ),
      },
      {
        path: 'notifiche/',
        element: (
          <Lazy>
            <NotifichePage />
          </Lazy>
        ),
      },
      {
        path: 'audit/',
        element: (
          <Lazy>
            <AuditPage />
          </Lazy>
        ),
      },
      {
        path: 'import-csv/',
        element: (
          <Lazy>
            <ImportCsvPage />
          </Lazy>
        ),
      },
      {
        path: 'importatori/',
        element: (
          <Lazy>
            <ImportatoriPage />
          </Lazy>
        ),
      },
      {
        path: 'configuratore-itinerari/',
        element: (
          <Lazy>
            <ConfiguratoreItinerari />
          </Lazy>
        ),
      },
      {
        path: 'vocabolari/',
        element: (
          <Lazy>
            <VocabolariPage />
          </Lazy>
        ),
      },
      {
        path: 'utenti/',
        element: (
          <Lazy>
            <UtentiPage />
          </Lazy>
        ),
      },
    ],
  },
];
